/**
 * Client API InfraNet GN — V2 (plan de mise en production §3, vague V1).
 *
 * REBRANCÉ sur l'API V2 : base `/api/v1/`, enveloppe `{data, meta, errors}`,
 * jeton porteur OIDC, concurrence optimiste If-Match. Les 18 pages qui
 * importent getList/getDetail/post/patch/login continuent de fonctionner
 * sans modification : les chemins plugin sont traduits automatiquement.
 *
 * La bascule `V2_API_ACTIVE` a été retirée : la v1 est archivée, il n'y a plus rien vers quoi
 * revenir. La garder donnait un faux confort — un interrupteur qui, actionné, aurait envoyé la
 * console vers un serveur éteint — et surtout elle gardait en vie un chemin d'authentification
 * par mot de passe que la console ne doit plus jamais emprunter.
 */
import { purgeOfflineScope } from "@/offline/db";
import { EVENEMENT_SESSION_PERDUE, startOIDCLogin } from "@/api/v2client";

const V2_BASE = "/api/v1";
const TOKEN_KEY = "infranet_token";
const USER_SCOPE_KEY = "infranet_user_scope";
const OFFLINE_PROFILE_KEY = "infranet_offline_profile";

// ─── Correspondance chemins plugin v1 → V2 ────────────────────────────

const CHEMINS_V2: Record<string, string> = {
  // Radio — le routeur monte « radio/… », pas « radio/radio/… ».
  "/plugins/fiberasset/base-stations": "/radio/bases",
  "/plugins/fiberasset/radio-sites": "/radio/sites",
  "/plugins/fiberasset/sectors": "/radio/sectors",
  "/plugins/fiberasset/cpe": "/radio/cpes",
  "/plugins/fiberasset/coverage": "/radio/coverages",
  "/plugins/fiberasset/ptp-links": "/radio/links",
  // Opérations — incidents et ordres de travail sont montés à la racine de l'API.
  "/plugins/fiberasset/incidents": "/incidents",
  "/plugins/fiberasset/work-orders": "/work-orders",
  "/plugins/fiberasset/inspections": "/ops/inspections",
  // Fibre
  "/plugins/fiberasset/segments": "/fiber/segments",
  "/plugins/fiberasset/routes": "/fiber/routes",
  "/plugins/fiberasset/supports": "/fiber/supports",
  "/plugins/fiberasset/cables": "/fiber/cables",
  // GPON
  "/plugins/fiberasset/olts": "/access/olts",
  "/plugins/fiberasset/pon-ports": "/access/pon-ports",
  "/plugins/fiberasset/splitters": "/access/splitters",
  "/plugins/fiberasset/onts": "/access/onts",
  // Tiers, énergie, terrain, qualité
  "/plugins/fiberasset/suppliers": "/contract/suppliers",
  "/plugins/fiberasset/contracts": "/contract/contracts",
  "/plugins/fiberasset/power-sources": "/power/sources",
  "/plugins/fiberasset/field-missions": "/field/missions",
  "/plugins/fiberasset/quality": "/quality/quality-rules",
  // Sites et actifs
  "/plugins/fiberasset/sites": "/sites",
  "/plugins/fiberasset/assets": "/assets",
};

/**
 * Chemins v1 SANS équivalent V2. Les traduire au jugé produirait un 404 muet : on préfère
 * une erreur qui nomme le chemin et dit quoi faire.
 *
 * `cables/states` n'existe plus : l'état des câbles vient désormais de la carte, qui le
 * calcule côté serveur — `GET /api/v1/map/features?layers=cables`.
 */
const CHEMINS_SANS_EQUIVALENT: Record<string, string> = {
  "/plugins/fiberasset/cables/states":
    "utiliser GET /api/v1/map/features?layers=cables (la couleur et l'état viennent du serveur)",
  "/plugins/fiberasset/alarms": "corrélation d'alarmes non portée par la V2 (phase 2)",
};

/**
 * Traduit un chemin v1 (plugin NetBox) en chemin V2.
 * Les chemins qui ne sont pas dans la table passent tels quels.
 */
export function resolvePath(path: string): string {
  // chercher la correspondance la plus longue d'abord
  for (const [old, v2] of Object.entries(CHEMINS_V2).sort((a, b) => b[0].length - a[0].length)) {
    if (path.startsWith(old)) {
      return `${V2_BASE}${v2}${path.slice(old.length)}`;
    }
  }
  // Un chemin plugin non traduit ne doit PAS être préfixé au hasard : il partirait en 404
  // sans que personne sache pourquoi. On échoue en nommant le chemin et le remplaçant.
  for (const [ancien, raison] of Object.entries(CHEMINS_SANS_EQUIVALENT)) {
    if (path.startsWith(ancien)) {
      throw new Error(`Chemin v1 sans équivalent V2 : ${ancien} — ${raison}`);
    }
  }
  if (path.startsWith("/plugins/")) {
    throw new Error(
      `Chemin v1 non traduit : ${path}. L'ajouter à CHEMINS_V2 après avoir vérifié la route ` +
        `réelle dans API.md, ou à CHEMINS_SANS_EQUIVALENT si elle n'existe pas.`,
    );
  }
  if (path.startsWith("/api/v1/")) return path;
  if (!path.startsWith("/api/")) return `${V2_BASE}${path}`;
  return path;
}

// ─── Enveloppe V2 §4.1 ────────────────────────────────────────────────

interface Envelope<T> {
  data: T;
  meta: { request_id: string; page?: number; page_size?: number; total?: number };
  // Le socle renvoie {code, champ, message} — noms français, gestionnaire d'erreurs V2.
  // Lire `detail`/`field` fait perdre le message ET le champ visé.
  errors: { code: string; champ: string | null; message: string }[];
}

// ─── Erreur ────────────────────────────────────────────────────────────

export class ApiError extends Error {
  readonly status: number;
  readonly detail: string;
  readonly path: string;
  readonly fieldErrors: Record<string, string>;

  readonly request_id: string;

  constructor(
    status: number,
    detail: string,
    path: string,
    fieldErrors: Record<string, string> = {},
    request_id = "",
  ) {
    super(`${path} — ${status} ${detail}`);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
    this.path = path;
    this.fieldErrors = fieldErrors;
    this.request_id = request_id;
  }

  get isForbidden(): boolean {
    return this.status === 401 || this.status === 403;
  }

  get isUnauthenticated(): boolean {
    if (this.status === 401) return true;
    return (
      this.status === 403 &&
      /invalid token|authentication credentials|user inactive|bearer/i.test(this.detail)
    );
  }

  get isConflict(): boolean {
    return this.status === 412;
  }

  get isNetwork(): boolean {
    return this.status === 0;
  }
}

// ─── Session et jeton ─────────────────────────────────────────────────

function getToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  sessionStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(USER_SCOPE_KEY);
  localStorage.removeItem(OFFLINE_PROFILE_KEY);
}

export function getUserScope(): string | null {
  return sessionStorage.getItem(USER_SCOPE_KEY);
}

export function isAuthenticated(): boolean {
  return getToken() !== null;
}

// Déclaré dans `v2client` et réexporté ici : deux constantes du même nom finiraient par
// diverger, et l'application ne réagirait qu'à celle que l'écran fautif a employée.
export { EVENEMENT_SESSION_PERDUE } from "@/api/v2client";

interface OfflineProfile {
  profileId: string;
  ownerHash: string;
}

function readOfflineProfile(): OfflineProfile | null {
  try {
    const raw = localStorage.getItem(OFFLINE_PROFILE_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<OfflineProfile>;
    return typeof value.profileId === "string" && typeof value.ownerHash === "string"
      ? { profileId: value.profileId, ownerHash: value.ownerHash }
      : null;
  } catch {
    return null;
  }
}

async function hashUsername(username: string): Promise<string> {
  const normalized = username.trim().toLocaleLowerCase("fr");
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(normalized));
  return [...new Uint8Array(digest)]
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("");
}

// ─── Délai réseau ─────────────────────────────────────────────────────

export const DELAI_MAX_MS = 45_000;

function signalAvecDelai(signalAppelant: AbortSignal | null | undefined) {
  const controleur = new AbortController();
  let parDelai = false;
  const minuterie = setTimeout(() => {
    parDelai = true;
    controleur.abort();
  }, DELAI_MAX_MS);
  const relayer = () => controleur.abort();
  signalAppelant?.addEventListener("abort", relayer);
  return {
    signal: controleur.signal,
    expire: () => parDelai,
    liberer: () => {
      clearTimeout(minuterie);
      signalAppelant?.removeEventListener("abort", relayer);
    },
  };
}

// ─── Requête générique ────────────────────────────────────────────────

type RequestOptions = RequestInit & {
  anonymous?: boolean;
  ifMatch?: string;
};

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { anonymous, ifMatch, ...init } = options;
  const token = anonymous ? null : getToken();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init.headers as Record<string, string>),
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (ifMatch) {
    headers["If-Match"] = ifMatch;
  }

  const delai = signalAvecDelai(init.signal);
  let resp: Response;
  try {
    resp = await fetch(resolvePath(path), { ...init, headers, signal: delai.signal });
  } catch (cause) {
    throw new ApiError(
      0,
      delai.expire()
        ? `le serveur n'a pas répondu en ${Math.round(DELAI_MAX_MS / 1000)} s`
        : `réseau injoignable (${cause instanceof Error ? cause.message : "cause inconnue"})`,
      path,
    );
  } finally {
    delai.liberer();
  }

  if (!resp.ok) {
    let detail = resp.statusText;
    let fieldErrors: Record<string, string> = {};
    let request_id = "";
    try {
      const body = await resp.json();
      // UI_STATES #4 : l'identifiant de requête accompagne toute erreur, pour le support.
      request_id = body?.meta?.request_id ?? "";
      if (body.errors) {
        // enveloppe V2 : errors est toujours une liste
        detail = body.errors[0]?.detail ?? resp.statusText;
        for (const e of body.errors) {
          if (e.field) fieldErrors[e.field] = e.detail;
        }
      } else if (body.detail) {
        detail = body.detail;
      }
    } catch {
      // corps non JSON
    }
    const erreur = new ApiError(resp.status, detail, path, fieldErrors, request_id);
    if (erreur.isUnauthenticated && !anonymous) {
      clearToken();
      window.dispatchEvent(new CustomEvent(EVENEMENT_SESSION_PERDUE));
    }
    throw erreur;
  }

  // Débarrasser l'enveloppe et rendre `data` directement.
  const body = (await resp.json()) as Envelope<T>;
  return body.data;
}

// ─── Helpers compatibles v1 (les 18 pages ne changent pas) ────────────

/** GET d'une collection : `request` a déjà retiré l'enveloppe, `data` EST la liste. */
export async function getList<T>(path: string, params?: Record<string, string>): Promise<T[]> {
  const qs = params ? `?${new URLSearchParams(params)}` : "";
  return request<T[]>(`${path}${qs}`);
}

/** GET détail */
export function getDetail<T>(path: string): Promise<T> {
  return request<T>(path);
}

/** POST */
export function post<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: "POST", body: JSON.stringify(body) });
}

/** PATCH partiel — file de deltas terrain (OPS-013). */
export function patch<T>(
  path: string,
  body: unknown,
  headers: Record<string, string> = {},
): Promise<T> {
  return request<T>(path, { method: "PATCH", body: JSON.stringify(body), headers });
}

// ─── Authentification ─────────────────────────────────────────────────

// Le nom de l'émetteur, les points d'entrée OIDC, le tirage du vérifiant PKCE et l'échange du
// code vivaient ici ET dans `v2client.ts`. Ils n'y vivent plus qu'une fois, dans `v2client`,
// qui traite le retour de Keycloak — c'est le retour qui fabrique la session.

/**
 * Authentification.
 *
 * V2 : OIDC PKCE → redirection Keycloak → callback → jeton porteur.
 * v1 : provisionnement de token NetBox (compatibilité).
 */
export async function login(): Promise<string> {
  // OIDC par redirection (PKCE). Le départ ET le retour vivent dans `v2client.ts`,
  // en UN seul exemplaire.
  //
  // Il y en avait deux, presque identiques : celui-ci lançait la redirection, celui de
  // `v2client` traitait le retour. Presque identiques suffit à casser — c'est ainsi que le
  // jeton s'est retrouvé rangé sous une clé que cette moitié-ci ne lisait pas, et que la
  // connexion réussie ramenait à l'écran de connexion. Deux implémentations d'un même
  // protocole ne restent jamais d'accord ; celle qui reste est celle qui traite le retour,
  // parce que c'est elle qui fabrique la session.
  await startOIDCLogin();
  // La fonction ne revient pas — le navigateur est déjà parti vers Keycloak.
  return "";
}

/**
 * Ouvre le périmètre local après authentification : identifiant de coffre hors connexion, et
 * purge si l'appareil change de porteur.
 *
 * Elle n'était plus appelée depuis le passage à OIDC — l'ancienne connexion par mot de passe
 * était son unique appelant. `getUserScope()` rendait donc toujours `null`, et l'écran terrain
 * répondait « Reconnectez-vous avant de télécharger une mission » à un utilisateur pourtant
 * connecté. Aucune erreur nulle part : simplement une clé jamais posée.
 */
export async function ouvrirSessionLocale(username: string, token: string): Promise<void> {
  const ownerHash = await hashUsername(username);
  let profile = readOfflineProfile();
  if (profile && profile.ownerHash !== ownerHash) {
    await purgeOfflineScope(profile.profileId);
    profile = null;
  }
  profile ??= { profileId: crypto.randomUUID(), ownerHash };
  setToken(token);
  sessionStorage.setItem(USER_SCOPE_KEY, profile.profileId);
  localStorage.setItem(OFFLINE_PROFILE_KEY, JSON.stringify(profile));
}
